import { Film, Mic, MessagesSquare, Target, HelpCircle, NotebookPen, FolderOpen } from "lucide-react";
import { PageHero, Container, SectionHead, CtaBand } from "@/components/site/blocks";
import { PILLARS } from "@/components/site/Pillars";
import { Reveal } from "@/components/site/motion";
import { useSeo } from "@/components/site/useSeo";

const ACTIVITIES = [
  { icon: Film, t: "Documentary screening", d: "A shared viewing of John's story, presented with captions." },
  { icon: Mic, t: "Keynote", d: "John speaks directly about resilience, faith, independence, and purpose." },
  { icon: MessagesSquare, t: "Facilitated discussion", d: "Guided conversation that connects the film to participants' own lives." },
  { icon: Target, t: "Goal setting", d: "Participants name one education or employment goal and a first step." },
  { icon: HelpCircle, t: "Q&A", d: "Open time for honest questions with John." },
  { icon: NotebookPen, t: "Reflection worksheet", d: "A simple take-home page to capture thoughts and next steps." },
  { icon: FolderOpen, t: "Facilitator materials", d: "Support for staff who lead or continue the conversation." },
];

export default function Program() {
  useSeo("The Program", "Explore JEM's Journey • Empower • Motivate framework and activities: documentary screening, keynote, discussion, goal setting, Q&A, and facilitator materials.");
  return (
    <>
      <PageHero testid="program-hero" label="The Program" lines={["From story", <span key="s" className="italic text-[#F3C649]">to next steps.</span>]}
        intro="JEM moves people through three connected parts. Each one builds on John's documentary and ends with something practical." />
      <section className="py-20 sm:py-28" data-testid="program-pillars">
        <Container className="space-y-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.05}>
              <article className="group grid items-center gap-6 border-t border-[#D4AF37]/25 py-10 md:grid-cols-12" data-testid={`program-pillar-${p.key}`}>
                <span className="font-mono-label text-sm text-[#F3C649] md:col-span-1">{p.n}</span>
                <h2 className="font-display text-6xl font-semibold transition-transform duration-500 group-hover:translate-x-3 sm:text-7xl md:col-span-5">{p.word}</h2>
                <p className="text-lg leading-relaxed text-[#C8C5BB] md:col-span-6">{p.text}</p>
              </article>
            </Reveal>
          ))}
        </Container>
      </section>
      <section className="bg-[#07080A] py-24 sm:py-32" data-testid="program-activities">
        <Container>
          <SectionHead label="Possible activities" title="Choose what fits your group." intro="Partners can combine any of these elements." />
          <ul className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ACTIVITIES.map((a, i) => (
              <Reveal key={a.t} delay={(i % 4) * 0.06}>
                <li className="h-full rounded-3xl border border-[#D4AF37]/20 bg-[#12141C] p-7 transition-colors duration-500 hover:border-[#F3C649]/60">
                  <a.icon size={24} aria-hidden="true" className="text-[#F3C649]" />
                  <h3 className="mt-8 font-display text-2xl font-semibold">{a.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#C8C5BB]">{a.d}</p>
                </li>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <li className="flex h-full flex-col justify-end rounded-3xl bg-gradient-to-br from-[#F3C649] to-[#B8932A] p-7 text-[#0A0B0E]">
                <h3 className="font-display text-2xl font-bold">Shaped around you</h3>
                <p className="mt-3 text-sm font-medium leading-relaxed">Each partnership can be shaped around your audience and organization.</p>
              </li>
            </Reveal>
          </ul>
        </Container>
      </section>
      <CtaBand title="Let's shape a session for your audience." />
    </>
  );
}
